import React, { useState, useEffect } from 'react';
import { Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import AccountMultipleOutlineIcon from 'mdi-react/AccountMultipleOutlineIcon';
import AccountRemoveOutlineIcon from 'mdi-react/AccountRemoveOutlineIcon';
import LoadingIcon from 'mdi-react/LoadingIcon';
import Api from '../../../util/api';
import { partial } from '../../../util/translation/translation';
import Loading from '../../../shared/components/custom/Loading';
import FormCard from '../../../shared/components/custom/FormCard';
import FormModal from '../../../shared/components/custom/FormModal';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';
import AddRoleForm from './AddRoleForm';
import useConfig from '../../../util/useConfig';

const initials = (user) =>
  `${user.firstname?.[0] || ''}${user.lastname?.[0] || ''}`.toUpperCase() ||
  '?';

const UserRolesTab = () => {
  const config = useConfig();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  // Users in the open role; `busy` dims the modal while it saves or reloads
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  // Number of users per role name, filled in after the roles load
  const [counts, setCounts] = useState({});
  const u = partial('CreateUserPage');
  const p = partial('UserRolesTab');
  const r = partial('UserRoles');
  const s = partial('shared');

  const loadCounts = async (_roles) => {
    const entries = await Promise.all(
      _roles.map(async (role) => {
        try {
          const data = await Api.users.getUsersByRoleName(role.name, config);
          return [role.name, data.length];
        } catch {
          return [role.name, undefined];
        }
      })
    );
    setCounts(Object.fromEntries(entries));
  };

  const getRoles = async () => {
    setLoading(true);
    try {
      const data = await Api.users.getAvailableRoles();
      setRoles(data);
      loadCounts(data);
    } catch (e) {
      toast.error(s('somethingWentWrong'));
    }
    setLoading(false);
  };

  const loadUsers = async (role) => {
    const data = await Api.users.getUsersByRoleName(role.name, config);
    setUsers(data);
    setCounts((prev) => ({ ...prev, [role.name]: data.length }));
  };

  const handleShowUsers = async (role) => {
    setUsers([]);
    setCurrentRole(role);
    setBusy(true);
    try {
      await loadUsers(role);
    } catch (e) {
      toast.error(s('somethingWentWrong'));
      setCurrentRole(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    getRoles();
    // eslint-disable-next-line
  }, []);

  const handleAssignRoles = async (user, assign) => {
    setBusy(true);
    try {
      await Api.users.updateUserRoles(
        user.teamUserId,
        currentRole.name,
        assign
      );
      toast.success(
        assign ? u('userWasAssignedRoles') : p('roleRemovedFromUser')
      );
    } catch (err) {
      toast.error(err || u('errorGivingUserPolicies'));
    }
    try {
      await loadUsers(currentRole);
    } catch (e) {
      toast.error(s('somethingWentWrong'));
    } finally {
      setBusy(false);
    }
  };

  const handleAddRoleSubmit = async ({ selectedUser }) => {
    if (
      users.some((user) => user.userCloudId === selectedUser.value.userCloudId)
    ) {
      toast.warning(p('userAlreadyHasRole'));
      return;
    }
    await handleAssignRoles({ ...selectedUser.value }, true);
  };

  const closeModal = () => setCurrentRole(null);

  const renderUsersModal = () => (
    <FormModal
      isOpen={!!currentRole}
      onClose={closeModal}
      className="role-modal"
      title={currentRole ? r(currentRole.name) : ''}
      subtitle={currentRole ? r(`${currentRole.name}Description`) : null}
    >
      <div className="role-modal__add">
        <AddRoleForm onSubmit={handleAddRoleSubmit} disabled={busy} />
      </div>

      <div className="role-modal__head">
        <span>{s('users')}</span>
        {busy ? (
          <LoadingIcon className="stats-spinner role-modal__spinner" />
        ) : (
          <span className="form-card__count">{users.length}</span>
        )}
      </div>
      <div
        className={`role-modal__list${busy ? ' role-modal__list--busy' : ''}`}
      >
        {!busy && !users.length && (
          <p className="role-modal__empty">{p('noUsersWithRole')}</p>
        )}
        {users.map((user) => (
          <div className="role-user" key={`user${user.id}`}>
            <span className="role-user__avatar">{initials(user)}</span>
            <div className="role-user__text">
              <Link
                to={`/users/edit/${user.userCloudId}`}
                className="role-user__name"
              >
                {`${user.firstname} ${user.lastname}`}
              </Link>
              <span className="role-user__phone">{user.phoneNumber}</span>
            </div>
            <SquareIconButton
              icon={<AccountRemoveOutlineIcon />}
              title={p('removeRole')}
              tone="danger"
              id={`remove-role-${user.id}`}
              disabled={busy}
              onClick={() => handleAssignRoles(user, false)}
            />
          </div>
        ))}
      </div>
    </FormModal>
  );

  return (
    <>
      <Loading loading={loading} />
      <FormCard
        title={
          <>
            {s('roles')}
            <span className="form-card__count">{roles.length}</span>
          </>
        }
        bodyClassName="form-card__body--flush"
        className="mt-3"
      >
        <Table responsive striped className="table--roomy table--stack mb-0">
          <thead>
            <tr>
              <th>{s('name')}</th>
              <th>{s('description')}</th>
              <th>{s('users')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => (
              <tr
                key={role.id}
                className="role-row"
                onClick={() => handleShowUsers(role)}
              >
                <td className="table-stack__primary">
                  <strong className="font-weight-500">{r(role.name)}</strong>
                </td>
                <td data-label={s('description')} className="text-muted">
                  {r(`${role.name}Description`)}
                </td>
                <td data-label={s('users')}>
                  <span
                    className={`qb-chip${
                      counts[role.name] ? ' role-row__count--on' : ''
                    }`}
                  >
                    {counts[role.name] ?? '–'}
                  </span>
                </td>
                <td className="table-stack__actions">
                  <div className="icon-actions justify-content-end">
                    <SquareIconButton
                      icon={<AccountMultipleOutlineIcon />}
                      title={p('manageUsers')}
                      id={`role-users-${role.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShowUsers(role);
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </FormCard>
      {renderUsersModal()}
    </>
  );
};

export default UserRolesTab;
